// COP — the host-owned SINGLE VISIBLE PROJECTION (CONTRACT:C1-MAP-CLIENT.1.0 +
// CONTRACT:I4-POLICY-DECISION.1.0). Spec: docs/AOA-VISIBILITY-PROJECTION.md.
//
// THE SPINE (migration Steps 0-2 — SHADOW MODE). This object LAYERS ON the existing
// CopPdp (the CLS_ID engine stays underneath as the fast oracle — pdp.ts is NOT
// modified). It owns ONE Map<id, VisibleRow> of the rows visible to the CURRENT
// subject column, maintained incrementally:
//
//   - onRecord(env): record into the PDP, read the visibility bit via the EXISTING
//     fast path (pdp.isVisibleCached — the SAME bit the data seam uses, cop.ts:640),
//     and maintain the row. A byte-equal republish (same id + same marking) yields
//     NO delta (O(1) movement firehose — load-bearing for the 5k perf goal). The row
//     stores the ANNOTATED env (annotateForRender via pdp.evaluate) so paint is
//     uniform — the projection OWNS the annotation (the red-team's annotation-
//     divergence hole: tracksCapability paints annotateForRender, multiInt paints the
//     raw env; the projection makes paint uniform by construction).
//
//   - onSubject(claims): switch the PDP identity, then reproject() — diff ALL
//     recorded ids against the new subject column and emit the batched add/remove
//     delta. THIS is the move that (in a LATER increment) retires refilterAll /
//     refilterKinds / RENDERED_KINDS; here it must just be CORRECT in shadow.
//
// All faces are PURE FOLDS of the one Map (O(visible) reads, never a per-object
// re-decide): renderedRows()/renderedIds() (paint set), bannerBasis() (== today
// pdp.visibleEnvelopes() for the banner), censusBasis() (recomputeShell), and
// findVisible(id) (the Glomar lookup). subscribe(cb) feeds delta consumers.
//
// SHADOW-MODE DISCIPLINE: this object does NOT drive the engine, does NOT mutate any
// status.*, and does NOT touch the CLS_ID engine internals. It is pure observation
// that runs in parallel with the production paths so the coherence gate can assert
// the 4-way id-set equality the AOA's runtime invariant demands.

import type { CanonicalEnvelope } from "../../../../packages/schema/ts/envelope.ts";
import type { SubjectClaims } from "../../../../packages/abac-pdp/src/index.ts";
import { annotateForRender, type CopPdp } from "./pdp.ts";

/** One row of the visible projection — what the host would paint for an id under the
 *  CURRENT subject. The `env` is ALREADY annotated (annotateForRender) so every face
 *  reads one uniform painted shape (no tracks-vs-multiInt annotation divergence). */
export interface VisibleRow {
  /** The object id (the engine entity id). */
  readonly id: string;
  /** The annotated envelope the host would upsert (uniform paint shape). */
  readonly env: CanonicalEnvelope;
}

/** A typed delta the projection emits to subscribers. `adds`/`replaces` carry the
 *  ANNOTATED env (so a consumer paints uniformly); `removes` carries only ids (the
 *  engine removes by id). Empty deltas are NEVER emitted (the O(1) firehose property:
 *  a byte-equal republish produces no delta, so no allocation hits the subscriber). */
export interface VisibleDelta {
  /** Rows newly visible (a new id, or an id that became visible). */
  readonly adds: VisibleRow[];
  /** Rows no longer visible (or removed) — by id only. */
  readonly removes: string[];
  /** Rows that stayed visible but whose painted env changed (re-mark, re-annotate). */
  readonly replaces: VisibleRow[];
  /** Monotonic epoch — bumped on every emitted (non-empty) delta. */
  readonly epoch: number;
}

/** The host-owned visible projection over an EXISTING CopPdp. */
export interface VisibleProjection {
  /** A publish: record + maintain the row. Byte-equal republish ⇒ no delta. */
  onRecord(env: CanonicalEnvelope): void;
  /** A subject / down-filter change: setIdentity + reproject ALL recorded ids. */
  onSubject(claims: SubjectClaims): void;
  /** Drop the ENTIRE projection (rows + the reproject diff-universe + recency stamps) AND
   *  the wrapped PDP's recorded picture, in LOCKSTEP — so the dual-authority pair the
   *  coherence gate watches stays equal (both empty) after a wipe. Used by a LIVE theater
   *  switch: the host removes the painted ids from the engine, then clears here so a later
   *  reproject can NEVER re-add the old theater. The operator's recency WINDOW (lo/hi/now)
   *  is preserved — clearing wipes THEATER data, not the operator's pane-of-glass setting. */
  clear(): void;
  /** The time owner moved (age-out tick / recency-width change / scrub): set the clock
   *  `now` + the recency window `[lo, hi]` over lastSeen, then reproject. A pane-of-glass
   *  filter — never fed to the source. `[-Infinity, +Infinity]` = all-time (no filtering). */
  onTime(now: number, lo: number, hi: number): void;

  // ── pure folds of the one Map (the faces) ──
  /** The paint set: every annotated row visible to the current subject. */
  renderedRows(): VisibleRow[];
  /** The paint id-set (the engine would paint exactly these ids). */
  renderedIds(): Set<string>;
  /** The banner high-water basis — the annotated visible envelopes (== today's
   *  pdp.visibleEnvelopes() id-set; the banner folds security off these). */
  bannerBasis(): CanonicalEnvelope[];
  /** The census/spine/catalog/pictureSummary basis (== bannerBasis; one source). */
  censusBasis(): CanonicalEnvelope[];
  /** Glomar-safe id lookup: the annotated env IFF it is visible, else null. */
  findVisible(id: string): CanonicalEnvelope | null;

  /** Subscribe to typed deltas. Returns an unsubscribe fn. */
  subscribe(cb: (delta: VisibleDelta) => void): () => void;

  /** Diagnostics: how many ids the projection has ever recorded (for the gate). */
  recordedCount(): number;
}

/** The internal row — the public VisibleRow plus the two cheap change-keys the hot
 *  paths compare on (so neither the firehose nor a reproject re-annotates or re-JSONs
 *  a row whose paint is unchanged):
 *   - `secKey`: a cheap byte-faithful serialization of the env's security (the SAME 6
 *     fields the CLS_ID engine's rawKey uses). Byte-equal ⇒ the marking is unchanged ⇒
 *     the firehose short-circuits BEFORE annotating (O(1) movement firehose).
 *   - `res`: the MEMOIZED VisibilityResult reference pdp.evaluate returns (one object
 *     per (CLS_ID, subject-column)). Same reference ⇒ byte-identical annotation ⇒ a
 *     reproject skips re-annotating that row (an O(1) reference compare, not a JSON). */
interface InternalRow extends VisibleRow {
  readonly secKey: string;
  readonly res: object;
}

/** Build a host-owned VisibleProjection over an EXISTING CopPdp. The PDP is the fast
 *  oracle (CLS_ID engine); this only maintains the visible Map + emits deltas. */
export function createVisibleProjection(pdp: CopPdp): VisibleProjection {
  /** id → the annotated visible row. ONLY visible ids live here (a hidden id is
   *  ABSENT — never a row, never a count, never a delta target → Glomar by
   *  construction: a denied id can appear in NO fold). */
  const rows = new Map<string, InternalRow>();
  /** id → the CANONICAL recorded env (the diff universe for reproject()). Maintained
   *  incrementally on EVERY onRecord (incl. the firehose path) so it always mirrors
   *  pdp's registry byte-for-byte — in particular a same-registry-SIZE re-mark of an
   *  existing id (UNCLASS→TOP_SECRET) updates the mirror to the NEW env, so reproject()
   *  can NEVER decide a stale (over-permissive) marking. This replaces the prior
   *  size-keyed lookupCache, which kept the OLD env on a same-size re-mark and let an
   *  upgraded object stay visible to a now-lower subject (a derived-state-drift leak —
   *  the exact bug class the projection exists to delete, via a second cache). */
  const recorded = new Map<string, CanonicalEnvelope>();

  // ── THE TIME-WINDOW (recency) axis (Increment 4 — fold staleness into the projection,
  //    docs/AOA-STALENESS-TIME-WINDOW.md). The projection is now the SINGLE membership
  //    authority for BOTH clearance AND recency, so census == paint by construction. A
  //    just-in-time, PANE-OF-GLASS filter: the registry keeps everything; the window only
  //    decides what is PAINTED — never fed back to the feed/source.
  //  - `lastSeen[id]` = the injected `now` at the id's last onRecord (true recency — when
  //    we last got an update; works on EVERY object, not only ones carrying valid_time).
  //  - the window is `[windowLo, windowHi]` over lastSeen: in-window iff
  //    windowLo <= lastSeen[id] <= windowHi. Live anchor ⇒ hi=+Infinity, lo = now − width.
  //    Scrub-to-past ⇒ a finite hi (the pinned anchor). Default [-Inf,+Inf] = all-time, so
  //    the projection is ZERO behavior change until the host sets a window (onTime). `nowMs`
  //    is the time owner's clock, injected (NOT Date.now()), so the gate is deterministic.
  const lastSeen = new Map<string, number>();
  let nowMs = 0;
  let windowLo = -Infinity;
  let windowHi = Infinity;
  /** Recency of an id = the injected `now` at its last onRecord. A just-recorded id with
   *  no stamp yet reads as `nowMs` (now-fresh — never spuriously aged out). */
  const freshnessOf = (id: string): number => lastSeen.get(id) ?? nowMs;
  /** Is the id within the operator's recency window? (the time half of paint membership) */
  const inWindow = (id: string): boolean => {
    const t = freshnessOf(id);
    return t >= windowLo && t <= windowHi;
  };

  const subs = new Set<(delta: VisibleDelta) => void>();
  let epoch = 0;

  /** Emit a delta IFF it carries any change. The empty-delta short-circuit is the
   *  load-bearing O(1) firehose property: a byte-equal republish that changes no row
   *  allocates nothing and notifies no subscriber. */
  const emit = (adds: VisibleRow[], removes: string[], replaces: VisibleRow[]): void => {
    if (adds.length === 0 && removes.length === 0 && replaces.length === 0) return;
    epoch++;
    const delta: VisibleDelta = { adds, removes, replaces, epoch };
    for (const cb of subs) cb(delta);
  };

  /** A cheap, byte-faithful serialization of the security fields the decision (and the
   *  badge) depend on — the SAME 6 fields the CLS_ID engine's rawKey uses. Byte-equal
   *  ⇒ marking unchanged. NO normalization (so it can never merge what the evaluator
   *  splits). Geometry/time are intentionally EXCLUDED: a position-only republish keeps
   *  this key identical ⇒ the firehose short-circuits (no annotate, no delta). */
  const secKeyOf = (env: CanonicalEnvelope): string => {
    const s = env.security as unknown as Record<string, unknown>;
    return JSON.stringify([
      s.classification, s.releasability, s.caveats, s.mission,
      s.payload_mode, s.leakage_profile,
    ]);
  };

  /** Build the annotated (uniform paint) row, carrying the two cheap change-keys. One
   *  pdp.evaluate (the CLS_ID memo) yields BOTH the visible bit and the annotation —
   *  so neither path double-resolves. The stored `res` is the MEMOIZED result object
   *  reference (same (CLS_ID, column) ⇒ same reference ⇒ same annotation). */
  const buildRow = (env: CanonicalEnvelope, secKey: string): { row: InternalRow; visible: boolean } => {
    const res = pdp.evaluate(env);
    const row: InternalRow = { id: env.id, env: annotateForRender(env, res), secKey, res };
    return { row, visible: res.presentation.visible };
  };

  /** Re-evaluate PAINT MEMBERSHIP (clearance ∩ recency) for ALL recorded ids against the
   *  CURRENT subject column + time window, and emit one batched add/remove/replace delta.
   *  Shared by onSubject (claims changed) and onTime (the clock/window changed). The diff is
   *  over IDS — a new kind can never be forgotten. On a pure time tick the clearance result
   *  is unchanged (prev.res === res), so only aged-out removes + newly-in-window adds emit. */
  const reproject = (): void => {
    const adds: VisibleRow[] = [];
    const removes: string[] = [];
    const replaces: VisibleRow[] = [];
    for (const [id, env] of recorded) {
      const res = pdp.evaluate(env); // rides the CLS_ID memo (≤K decides)
      const prev = rows.get(id);
      const inPic = res.presentation.visible && inWindow(id);
      if (!inPic) {
        if (prev) {
          rows.delete(id);
          removes.push(id);
        }
        continue;
      }
      // O(1) ANNOTATION SKIP: already painted with the same memoized result ⇒ no change
      // (a reference compare, not a JSON). This is the common case on a time tick.
      if (prev && prev.res === res) continue;
      const row: InternalRow = { id, env: annotateForRender(env, res), secKey: prev?.secKey ?? secKeyOf(env), res };
      if (!prev) {
        rows.set(id, row);
        adds.push(row);
      } else {
        rows.set(id, row);
        replaces.push(row);
      }
    }
    emit(adds, removes, replaces);
  };

  return {
    onRecord(env: CanonicalEnvelope): void {
      // 1) Record into the PDP registry (the full picture) — the engine is the fast
      //    oracle; record() interns the CLS_ID and is the SOLE writer of (registry,
      //    objToCls). The projection never re-implements that.
      pdp.record(env);
      // Mirror the CANONICAL env on EVERY publish — BEFORE the firehose short-circuit —
      // so the reproject() diff universe always tracks the latest marking for this id
      // (a same-size re-mark can never leave a stale over-permissive env behind).
      recorded.set(env.id, env);
      // Stamp RECENCY on EVERY publish (incl. the firehose path, before the short-circuit):
      // a re-publish IS "seen now", so a position-only move keeps the dot in-window.
      lastSeen.set(env.id, nowMs);

      const prev = rows.get(env.id);
      const secKey = secKeyOf(env);

      // O(1) MOVEMENT FIREHOSE: an already-PAINTED id re-published with byte-equal security
      // (a position-only move is the common case) short-circuits HERE — no pdp.evaluate, no
      // annotateForRender, no delta. Its lastSeen was just refreshed (above), so it stays
      // in-window; a publish only ever REFRESHES recency (never ages an object out), so
      // there is no membership change. (Aging OUT is the onTime tick's job, not a publish's.)
      if (prev && prev.secKey === secKey) return;

      // 2) Build the row (one evaluate yields the bit AND the annotation — the SAME
      //    three-case taxonomy + memoized result the data seam's isVisibleCached uses).
      const { row, visible } = buildRow(env, secKey);
      // PAINT MEMBERSHIP = clearance ∩ recency. A cleared object published already past the
      // window (lastSeen < windowLo — e.g. a replayed old corpus object) is NOT painted.
      const inPic = visible && inWindow(env.id);

      if (!inPic) {
        // Hidden (by clearance) OR out-of-window. If it was painted, it's a remove; else a
        // no-op (Glomar: a denied id leaves NO trace; an out-of-window id is just filtered).
        if (prev) {
          rows.delete(env.id);
          emit([], [env.id], []);
        }
        return;
      }
      if (!prev) {
        rows.set(env.id, row);
        emit([row], [], []);
        return;
      }
      // Marking changed but still visible ⇒ a paint-relevant replace.
      rows.set(env.id, row);
      emit([], [], [row]);
    },

    onSubject(claims: SubjectClaims): void {
      // A persona / down-filter change. Switch the PDP identity (the engine reuses the
      // cached column if the profile is known — switch-back is free), then REPROJECT all
      // recorded ids against the new subject column ∩ the current time window. The diff is
      // over IDS, not kinds — a new capability kind can never be forgotten (the Phase-4 fix).
      pdp.setIdentity(claims);
      reproject();
    },

    clear(): void {
      // Wipe the visible rows, the reproject diff-universe, and the recency stamps — then
      // clear the wrapped PDP in lockstep so the two derived-state authorities stay equal
      // (both empty). NO delta is emitted here: the host has already removed the painted
      // ids from the engine via its sole-writer path (applyDeltaToEngine), so emitting a
      // removes-delta would be redundant. The recency window (nowMs/windowLo/windowHi) is
      // intentionally untouched — it is operator state, not theater state.
      rows.clear();
      recorded.clear();
      lastSeen.clear();
      pdp.clear();
    },

    onTime(now: number, lo: number, hi: number): void {
      // THE TIME OWNER MOVED (Increment 4): the age-out tick advanced `now`, or the operator
      // changed the recency WIDTH / scrubbed the anchor. Update the clock + window `[lo,hi]`
      // over lastSeen, then REPROJECT: aged-out ids drop, newly-in-window cleared ids appear.
      // census == paint stays a tautology (the projection is the single membership authority).
      // The registry is untouched — this is a pane-of-glass filter, never fed to the source.
      nowMs = now;
      windowLo = lo;
      windowHi = hi;
      reproject();
    },

    renderedRows(): VisibleRow[] {
      return [...rows.values()];
    },
    renderedIds(): Set<string> {
      return new Set(rows.keys());
    },
    bannerBasis(): CanonicalEnvelope[] {
      const out: CanonicalEnvelope[] = [];
      for (const r of rows.values()) out.push(r.env);
      return out;
    },
    censusBasis(): CanonicalEnvelope[] {
      // ONE source: census is the exact same fold as the banner basis (the AOA's
      // "all faces are folds of the one Map"). Kept as a distinct method so the gate
      // can assert renderedIds == bannerBasisIds == censusBasisIds explicitly.
      const out: CanonicalEnvelope[] = [];
      for (const r of rows.values()) out.push(r.env);
      return out;
    },
    findVisible(id: string): CanonicalEnvelope | null {
      const r = rows.get(id);
      return r ? r.env : null;
    },

    subscribe(cb: (delta: VisibleDelta) => void): () => void {
      subs.add(cb);
      return () => subs.delete(cb);
    },

    recordedCount(): number {
      return recorded.size;
    },
  };
}
